import { AnimateActionType } from "../hooks/useAnimateActions";

export type ProgressDuration = {
    [key in AnimateActionType]: number;
}

const progressDurationMinutes: ProgressDuration = {
    animate: 2.8,
    remove_background: 2.6,
    export: 2.4,
    crop_transparent: 2.0,
    split_frames: 1.0,
}

const PROGRESS_STEP_THRESHOLD = 0.004;

/**
 * Interpolates a value from 0.0 to 1.0 over a specified duration.
 * @param type The action type to determine the duration.
 * @param onUpdate A callback function receiving the current progress (0 to 1).
 * @returns A function to cancel the animation.
 */
export const getProgressPercent = (
    type: AnimateActionType,
    onUpdate: (progress: number) => void
): (() => void) => {
    const startTime = performance.now();
    const durationInMinutes = progressDurationMinutes[type];
    const durationMs = durationInMinutes * 60 * 1000;
    let animationFrameId: number;
    let lastReportedProgress = -1;

    const step = (currentTime: number) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / durationMs, 1.0);

        if (progress - lastReportedProgress >= PROGRESS_STEP_THRESHOLD || progress === 1.0) {
            onUpdate(progress);
            lastReportedProgress = progress;
        }

        if (progress < 1.0) {
            animationFrameId = requestAnimationFrame(step);
        }
    };

    animationFrameId = requestAnimationFrame(step);

    // Return cleanup function to stop animation if component unmounts
    return () => {
        if (animationFrameId) {
            cancelAnimationFrame(animationFrameId);
        }
    };
};

export interface ProgressMessage {
    time: number;
    message: string;
}

const animateProgressMessages: ProgressMessage[] = [
    {
        time: 0,
        message: `Preparing to animate...`
    },
    {
        time: 0.1,
        message: `Enhancing prompt...`
    },
    {
        time: 0.3,
        message: `Generating animation...`
    },
    {
        time: 0.7,
        message: `Finalizing animation...`
    },
    {
        time: 0.9,
        message: `Saving animation...`
    },
];

const removeBackgroundProgressMessages: ProgressMessage[] = [
    {
        time: 0,
        message: `Preparing to remove background...`
    },
    {
        time: 0.2,
        message: `Removing background...`
    },
    {
        time: 0.7,
        message: `Finalizing background removal...`
    },
    {
        time: 0.9,
        message: `Saving...`
    },
];

const exportProgressMessages: ProgressMessage[] = [
    {
        time: 0,
        message: `Preparing export...`
    },
    {
        time: 0.2,
        message: `Exporting...`
    },
    {
        time: 0.7,
        message: `Finalizing export...`
    },
    {
        time: 0.9,
        message: `Saving...`
    },
];

export const getProgressMessage = (type: AnimateActionType, progress: number): string => {
    let messages: ProgressMessage[];

    switch (type) {
        case 'animate':
            messages = animateProgressMessages;
            break;
        case 'remove_background':
            messages = removeBackgroundProgressMessages;
            break;
        case 'export':
            messages = exportProgressMessages;
            break;
        default:
            return '';
    }

    const currentMessage = [...messages]
        .reverse()
        .find((item) => progress >= item.time);

    return currentMessage ? currentMessage.message : messages[0].message;
};